<template>
  <Transition name="modal-fade">
    <div
      v-if="visible"
      class="modal-backdrop"
      role="dialog"
      aria-modal="true"
      :aria-label="STRINGS.tutorial.ruleChange.title"
      @keydown.esc="onAcknowledge"
    >
      <div class="modal-panel">
        <div class="modal-header">
          <span class="modal-icon" aria-hidden="true"><AppIcon name="clipboard" :size="24" /></span>
          <h2 class="modal-title font-display">{{ STRINGS.tutorial.ruleChange.title }}</h2>
        </div>

        <div class="modal-items">
          <div v-for="(item, i) in STRINGS.tutorial.ruleChange.items" :key="i" class="modal-item">
            <h3 class="modal-item-heading">{{ item.heading }}</h3>
            <p class="modal-item-body">{{ item.body }}</p>
          </div>
        </div>

        <div class="modal-actions">
          <button class="modal-see-rules" @click="onSeeRules">
            {{ STRINGS.tutorial.ruleChange.seeRules }}
          </button>
          <button class="modal-got-it" @click="onAcknowledge">
            {{ STRINGS.tutorial.ruleChange.gotIt }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import STRINGS from '@/constants/strings';
import AppIcon from '@/components/ui/icons/AppIcon.vue';

interface Props {
  visible: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  acknowledged: [];
  seeRules: [];
}>();

function onAcknowledge() {
  emit('acknowledged');
}

function onSeeRules() {
  emit('seeRules');
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9100;
  background: rgba(10, 10, 15, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-panel {
  background: var(--color-arena-surface, #1a1612);
  border: 2px solid rgba(201, 168, 76, 0.4);
  border-radius: 1rem;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.6),
    0 0 24px rgba(201, 168, 76, 0.1);
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-arena-border, #3d3428);
}

.modal-icon {
  font-size: 1.5rem;
  line-height: 1;
}

.modal-title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--color-energy-primary, #c9a84c);
  margin: 0;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.modal-items {
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.modal-item {
  background: rgba(201, 168, 76, 0.05);
  border: 1px solid rgba(201, 168, 76, 0.15);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.modal-item-heading {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-arena-text-bright, #f5e6c8);
  margin: 0 0 0.25rem;
}

.modal-item-body {
  font-size: 0.8rem;
  color: var(--color-arena-text-muted, #8a7e6a);
  margin: 0;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem 1.25rem;
  justify-content: flex-end;
}

.modal-see-rules {
  background: transparent;
  border: 1px solid var(--color-arena-border, #3d3428);
  color: var(--color-arena-text-muted, #8a7e6a);
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition:
    color 150ms,
    border-color 150ms;
  min-height: 36px;
}

.modal-see-rules:hover {
  color: var(--color-arena-text-bright, #f5e6c8);
  border-color: rgba(201, 168, 76, 0.3);
}

.modal-got-it {
  background: var(--color-energy-primary, #c9a84c);
  color: #1a1612;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0.5rem 1.25rem;
  transition:
    background 150ms,
    transform 100ms;
  min-height: 36px;
}

.modal-got-it:hover {
  background: #b89636;
  transform: translateY(-1px);
}

.modal-got-it:active {
  transform: translateY(0);
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 200ms ease;
}

.modal-fade-enter-active .modal-panel,
.modal-fade-leave-active .modal-panel {
  transition: transform 200ms ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-panel {
  transform: scale(0.95) translateY(8px);
}

.modal-fade-leave-to .modal-panel {
  transform: scale(0.95) translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .modal-fade-enter-active,
  .modal-fade-leave-active {
    transition: none;
  }
  .modal-fade-enter-active .modal-panel,
  .modal-fade-leave-active .modal-panel {
    transition: none;
  }
}
</style>
